import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Fingerprint, Camera, MapPin, Bell, WifiOff } from "lucide-react";

const CAPS = [
  ["biometric", "Biometric unlock", Fingerprint],
  ["camera", "Camera (document capture)", Camera],
  ["gps", "GPS / location", MapPin],
  ["push", "Push notifications", Bell],
  ["offline", "Offline-first + sync", WifiOff],
];
const TARGETS = ["web", "ios", "android", "ussd"];

export default function MobileTargets({ projectId }) {
  const [cfg, setCfg] = useState(null);
  const [targets, setTargets] = useState(["web"]);
  const [caps, setCaps] = useState({ biometric: false, camera: false, gps: false, push: false, offline: true });

  useEffect(() => { api.get(`/mobile/${projectId}/expo-config`).then(r => setCfg(r.data)).catch(() => {}); }, [projectId]);

  const saveTargets = async () => {
    try { await api.post("/mobile/targets", { project_id: projectId, targets }); toast.success("Build targets saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const qrValue = `exp://kadana-sandbox.expo/${projectId}`;

  return (
    <div data-testid="mobile-tab">
      <div className="k-container" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Smartphone size={18} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Build targets</h3>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {TARGETS.map(t => (
            <button key={t} data-testid={`mobile-target-${t}`} className={`k-badge ${targets.includes(t) ? "k-badge-accent" : ""}`}
              style={{ cursor: "pointer", textTransform: "uppercase" }}
              onClick={() => setTargets(x => x.includes(t) ? x.filter(y => y !== t) : [...x, t])}>{t}</button>
          ))}
          <button className="k-btn k-btn-primary" data-testid="mobile-save-targets" style={{ padding: "6px 14px", fontSize: 13 }} onClick={saveTargets}>Save targets</button>
        </div>
      </div>

      {/* #136 native capability toggles */}
      <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="mobile-capabilities">
        <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Native capabilities</h3>
        {CAPS.map(([k, label, Icon]) => (
          <label key={k} className="k-muted" data-testid={`cap-${k}`} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={caps[k]} onChange={e => setCaps({ ...caps, [k]: e.target.checked })} />
            <Icon size={15} /> {label}
          </label>
        ))}
        <p className="k-muted" style={{ fontSize: 11 }}>Capabilities use the platform's own APIs (no custom card fields). In-app account deletion is always included (Apple 5.1.1(v)).</p>
      </div>

      {/* #132 QR preview */}
      <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="mobile-qr">
        <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Open on your phone (Expo Go)</h3>
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ background: "#fff", padding: 12, borderRadius: 10 }} data-testid="qr-code">
            <QRCodeSVG value={qrValue} size={148} />
          </div>
          <div className="k-muted" style={{ fontSize: 13, maxWidth: 340 }}>
            Scan with the Expo Go app to live-test on your own device. <span className="k-badge k-badge-sandbox">SANDBOX</span>
            <div className="mono" style={{ fontSize: 11, marginTop: 8 }}>{qrValue}</div>
          </div>
        </div>
      </div>

      {cfg && (
        <div className="k-container" style={{ padding: 18 }} data-testid="expo-config">
          <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Expo config ({cfg.framework})</h3>
          <p className="k-muted" style={{ fontSize: 12 }}>{cfg.ownership}</p>
        </div>
      )}
    </div>
  );
}
